import { useRef, useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Eraser, Check } from "lucide-react";

/**
 * Canvas de assinatura digital.
 * Props:
 *  - onChange(dataUrl | null): chamado quando o utilizador desenha ou limpa
 *  - height: altura (default 140)
 *  - label: texto acima
 *  - required: se true, mostra asterisco
 */
export default function SignatureCanvas({ onChange, height = 140, label = "Assinatura", required = false, initialValue = null }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = height * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2;
    if (initialValue) {
      const img = new Image();
      img.onload = () => { ctx.drawImage(img, 0, 0, canvas.offsetWidth, height); setEmpty(false); };
      img.src = initialValue;
    }
  }, [height, initialValue]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches?.[0];
    return {
      x: (touch ? touch.clientX : e.clientX) - rect.left,
      y: (touch ? touch.clientY : e.clientY) - rect.top,
    };
  };

  const start = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };

  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    setEmpty(false);
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange?.(dataUrl);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setEmpty(true);
    onChange?.(null);
  };

  return (
    <div className="space-y-2" data-testid="signature-canvas">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </span>
        <Button type="button" size="sm" variant="outline" onClick={clear} disabled={empty} data-testid="signature-clear">
          <Eraser className="h-3 w-3 mr-1" /> Limpar
        </Button>
      </div>
      <div className="relative rounded-md border-2 border-dashed border-gray-300 bg-white overflow-hidden" style={{ height }}>
        <canvas
          ref={canvasRef}
          className="w-full h-full touch-none cursor-crosshair"
          style={{ height }}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
        {empty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-xs text-gray-400 italic">
            Assine com o dedo ou rato aqui
          </div>
        )}
        {!empty && (
          <div className="absolute bottom-1 right-2 text-emerald-600 flex items-center gap-1 text-xs">
            <Check className="h-3 w-3" /> Assinado
          </div>
        )}
      </div>
    </div>
  );
}
